import React, { useEffect } from "react";

import ThumbsDownIcon from "@uui/ThumbsDown";
import ThumbsUpIcon from "@uui/ThumbsUp";

import Typography from "@mui/material/Typography";

import { FEEDBACK_ICON_BY_TYPE } from "@app/analytics/components/reports/ReportFeedbackSnackbar";
import useMeetingReportUiStore from "@app/analytics/MeetingReport/stores/ui";
import { MeetingFeedbackResponse } from "@app/analytics/types";

import ConfirmationModal from "@rui/ConfirmationModal";
import { Column, Row } from "@rui/Stack";

import { FeedbackButton } from "./styles";

const MobileFeedbackDialog = ({
  autoSubmit,
  confirmationType,
  isLoading,
  show,
  pendingResponse,
  onSelect,
  onSubmit,
}: {
  autoSubmit: boolean;
  confirmationType: "success" | "error" | null;
  isLoading: boolean;
  pendingResponse: MeetingFeedbackResponse | null;
  show: boolean;
  onSelect: (response: MeetingFeedbackResponse) => void;
  onSubmit: (response: MeetingFeedbackResponse) => void;
}) => {
  const [dialog, setDialog] = useMeetingReportUiStore((state) => [
    state.dialog,
    state.setDialog,
  ]);

  useEffect(() => {
    if (autoSubmit && pendingResponse != null) {
      setDialog("feedback");
      onSubmit(pendingResponse);
    }
  }, [autoSubmit]);

  if (!show) return null;

  const closeDialog = () => setDialog(null);

  const handleConfirm = () => {
    if (!pendingResponse) return;
    onSubmit(pendingResponse);
  };

  const handleSelectResponse = (response: MeetingFeedbackResponse) => () =>
    onSelect(response);

  return (
    <ConfirmationModal
      title="Feedback"
      open={dialog === "feedback"}
      onClose={closeDialog}
      onConfirm={handleConfirm}
      cancelProps={{ show: false }}
      confirmProps={{
        label: "Submit",
        disabled: !pendingResponse,
        fullWidth: true,
      }}
      loading={isLoading}
      showHeaderCloseButton
      actionsContainerProps={
        confirmationType == null ? undefined : { show: false }
      }
      icon={FEEDBACK_ICON_BY_TYPE[confirmationType ?? "pending"]}
    >
      <Column spacing={2}>
        {confirmationType === "error" ? (
          <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
            Something went wrong and your feedback could not be submitted.
            Please refresh the page or try again later.
          </Typography>
        ) : confirmationType === "success" ? (
          <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
            Thanks for the feedback!
          </Typography>
        ) : (
          <>
            <Typography variant="bodyPrimaryMed" color="shades.neutral.600">
              Was this meeting a good use of your time?
            </Typography>
            <Row spacing={1.5} variant="center">
              <FeedbackButton
                className={
                  pendingResponse === "positive" ? "selected" : undefined
                }
                startIcon={<ThumbsUpIcon />}
                sx={{ flex: 1 }}
                onClick={handleSelectResponse("positive")}
              >
                Yes
              </FeedbackButton>
              <FeedbackButton
                className={
                  pendingResponse === "negative" ? "selected" : undefined
                }
                startIcon={<ThumbsDownIcon />}
                sx={{ flex: 1 }}
                onClick={handleSelectResponse("negative")}
              >
                No
              </FeedbackButton>
            </Row>
          </>
        )}
      </Column>
    </ConfirmationModal>
  );
};

export default MobileFeedbackDialog;
